import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CodeCard from "@/components/CodeCard";
import { useLang } from "@/lib/i18n";
import {
  AlertTriangle, CheckCircle2, XCircle, Info, Copy, Globe2, Map, Bot, FileText, Lock,
} from "lucide-react";
import { toast } from "sonner";

const SEVERITY_CLS = {
  critical: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const SITE_CHECK_ICONS = {
  https: Lock,
  robots_txt: Bot,
  search_bots: Globe2,
  sitemap: Map,
  llms_txt: FileText,
};

const scoreColor = (s) => (s >= 80 ? "text-emerald-600" : s >= 50 ? "text-amber-600" : "text-red-600");

const T = {
  en: {
    severity: { critical: "Critical", warning: "Warning", info: "Nice-to-have", pass: "Pass" },
    grade: { excellent: "Excellent", good: "Good", needs_work: "Needs work", poor: "Poor" },
    pagesScanned: (n) => `${n} pages scanned`,
    failed: (n) => ` - ${n} failed`,
    viaSitemap: "via sitemap",
    viaLinks: "via internal links",
    noIssues: "No page-level issues found across scanned pages.",
    issueSummary: (c, w, i) => `${c} critical, ${w} warning, ${i} minor issue group(s) to fix - start from the top.`,
    siteChecks: "Site-level checks",
    aiAccess: "AI crawler access",
    aiAllowed: (a, t) => `${a}/${t} AI bots allowed`,
    blocked: " - blocked: ",
    improvements: (n) => `Prioritized improvements (${n})`,
    improvementsSub: "Grouped by issue, ordered by impact. Fix criticals first to get AI-search ready.",
    noneFound: "No issues found on the scanned pages. Outstanding.",
    pageCount: (n) => `${n} page${n > 1 ? "s" : ""}`,
    affected: "Affected pages:",
    copied: "Fix snippet copied",
    perPage: "Per-page scores (lowest first)",
    thScore: "Score",
    thPage: "Page",
    thIssues: "Issues",
    thCritical: "Critical",
    noTitle: "no title",
    error: "Error",
    reportTitle: (d) => `Audit report - ${d}`,
  },
  fr: {
    severity: { critical: "Critique", warning: "Attention", info: "Optionnel", pass: "Réussi" },
    grade: { excellent: "Excellent", good: "Bon", needs_work: "À travailler", poor: "Faible" },
    pagesScanned: (n) => `${n} pages analysées`,
    failed: (n) => ` - ${n} en échec`,
    viaSitemap: "via sitemap",
    viaLinks: "via liens internes",
    noIssues: "Aucun problème au niveau des pages analysées.",
    issueSummary: (c, w, i) => `${c} critique(s), ${w} avertissement(s), ${i} mineur(s) à corriger - commencez par le haut.`,
    siteChecks: "Vérifications du site",
    aiAccess: "Accès des robots IA",
    aiAllowed: (a, t) => `${a}/${t} robots IA autorisés`,
    blocked: " - bloqués : ",
    improvements: (n) => `Améliorations priorisées (${n})`,
    improvementsSub: "Regroupées par problème, classées par impact. Corrigez les critiques d'abord pour être prêt pour la recherche IA.",
    noneFound: "Aucun problème trouvé sur les pages analysées. Remarquable.",
    pageCount: (n) => `${n} page${n > 1 ? "s" : ""}`,
    affected: "Pages affectées :",
    copied: "Correctif copié",
    perPage: "Scores par page (plus faibles d'abord)",
    thScore: "Score",
    thPage: "Page",
    thIssues: "Problèmes",
    thCritical: "Critiques",
    noTitle: "sans titre",
    error: "Erreur",
    reportTitle: (d) => `Rapport d'audit - ${d}`,
  },
};

export default function AuditReport({ result }) {
  const { lang } = useLang();
  const t = T[lang];

  const copyFix = async (fix) => {
    await navigator.clipboard.writeText(fix);
    toast.success(t.copied);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="mt-8 space-y-6">
      {/* Overall summary */}
      <Card>
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-5">
            <div className="flex items-baseline gap-2">
              <span className={`text-5xl font-heading font-semibold tabular-nums ${scoreColor(result.overall_score)}`} data-testid="site-audit-score">
                {result.overall_score}
              </span>
              <span className="text-muted-foreground text-sm">/100</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={SEVERITY_CLS[result.overall_score >= 80 ? "pass" : result.overall_score >= 50 ? "warning" : "critical"]}>
                  {t.grade[result.grade]}
                </Badge>
                <span className="text-sm font-medium">{result.domain}</span>
                <Badge variant="secondary" className="text-xs" data-testid="site-audit-pages-scanned">
                  {t.pagesScanned(result.pages_scanned)}{result.pages_failed > 0 ? t.failed(result.pages_failed) : ""}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {result.discovery_method === "sitemap" ? t.viaSitemap : t.viaLinks}
                </Badge>
              </div>
              <Progress value={result.overall_score} className="h-2 mt-3" />
              <p className="mt-2 text-xs text-muted-foreground">
                {result.issues.length === 0
                  ? t.noIssues
                  : t.issueSummary(
                      result.issues.filter((i) => i.severity === "critical").length,
                      result.issues.filter((i) => i.severity === "warning").length,
                      result.issues.filter((i) => i.severity === "info").length
                    )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Site-level checks */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">{t.siteChecks}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="site-audit-site-checks">
            {result.site_checks.map((c) => {
              const Icon = SITE_CHECK_ICONS[c.id] || Info;
              const ok = c.status === "pass";
              return (
                <div key={c.id} className="flex items-start gap-3 rounded-md border p-3">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${ok ? "bg-emerald-50 text-emerald-600" : c.status === "critical" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{c.label}</p>
                      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : c.status === "critical" ? <XCircle className="h-3.5 w-3.5 text-red-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 break-words">{c.message}</p>
                  </div>
                </div>
              );
            })}
            <div className="flex items-start gap-3 rounded-md border p-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
                <Bot className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium">{t.aiAccess}</p>
                <p className="text-xs text-muted-foreground mt-0.5" data-testid="site-audit-ai-access">
                  {t.aiAllowed(result.ai_access.allowed_ai, result.ai_access.total_ai)}
                  {result.ai_access.blocked_bots.length > 0 && `${t.blocked}${result.ai_access.blocked_bots.slice(0, 6).join(", ")}${result.ai_access.blocked_bots.length > 6 ? "..." : ""}`}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Prioritized improvements */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">{t.improvements(result.issues.length)}</CardTitle>
          <p className="text-sm text-muted-foreground">{t.improvementsSub}</p>
        </CardHeader>
        <CardContent>
          {result.issues.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t.noneFound}</p>
          ) : (
            <Accordion type="multiple" className="w-full" data-testid="site-audit-issues">
              {result.issues.map((issue, idx) => (
                <AccordionItem key={issue.id} value={issue.id}>
                  <AccordionTrigger className="text-sm hover:no-underline" data-testid={`site-audit-issue-${issue.id}`}>
                    <span className="flex items-center gap-2 text-left flex-wrap">
                      <span className="text-muted-foreground tabular-nums text-xs w-5">{idx + 1}.</span>
                      <Badge variant="outline" className={`${SEVERITY_CLS[issue.severity]} shrink-0 text-[10px]`}>{t.severity[issue.severity]}</Badge>
                      <span className="font-medium">{issue.label}</span>
                      <Badge variant="secondary" className="text-[10px]">{t.pageCount(issue.affected_count)}</Badge>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-sm text-muted-foreground">{issue.recommendation}</p>
                    {issue.fix && (
                      <div className="mt-3 rounded-md border code-scroll p-3 relative">
                        <pre className="font-mono text-xs whitespace-pre-wrap break-words pr-10">{issue.fix}</pre>
                        <Button variant="ghost" size="icon" className="absolute top-1.5 right-1.5 h-7 w-7" onClick={() => copyFix(issue.fix)} aria-label="Copy fix snippet" data-testid={`site-audit-copy-fix-${issue.id}`}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                    <div className="mt-3">
                      <p className="text-xs font-medium text-muted-foreground mb-1.5">{t.affected}</p>
                      <ul className="space-y-1">
                        {issue.pages.map((p, i) => (
                          <li key={i} className="text-xs text-muted-foreground flex gap-2">
                            <span className="h-1 w-1 rounded-full bg-primary shrink-0 mt-1.5" />
                            <span className="break-all">
                              <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{p.url}</a>
                              <span className="ml-1">- {p.message}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Per-page scores */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading">{t.perPage}</CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <div className="max-h-[520px] overflow-auto">
            <Table data-testid="site-audit-pages-table">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="pl-6">{t.thScore}</TableHead>
                  <TableHead>{t.thPage}</TableHead>
                  <TableHead>{t.thIssues}</TableHead>
                  <TableHead className="pr-6">{t.thCritical}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.pages.map((p, i) => (
                  <TableRow key={i} className="hover:bg-muted/40">
                    <TableCell className="pl-6">
                      {p.status === "ok" ? (
                        <span className={`font-heading font-semibold tabular-nums ${scoreColor(p.score)}`}>{p.score}</span>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">{t.error}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[380px]">
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline break-all line-clamp-1">{p.url}</a>
                      <span className="block text-[11px] text-muted-foreground line-clamp-1 mt-0.5">
                        {p.status === "ok" ? (p.title || <em>{t.noTitle}</em>) : p.error}
                      </span>
                    </TableCell>
                    <TableCell className="tabular-nums text-sm">{p.status === "ok" ? p.issues_count : "-"}</TableCell>
                    <TableCell className="pr-6">
                      {p.status === "ok" && p.critical_count > 0 ? (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">{p.critical_count}</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground tabular-nums">{p.status === "ok" ? "0" : "-"}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Downloadable report */}
      <CodeCard
        title={t.reportTitle(result.domain)}
        language="markdown"
        code={result.report_markdown}
        filename={`rang1-audit-${result.domain.replace(/[^a-z0-9.-]/gi, "")}.md`}
        testIdPrefix="site-audit-report"
        maxHeight="420px"
      />
    </motion.div>
  );
}
